import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { ModalBody, ModalHeader } from '@lightspeed/flame/Modal';
import { Radio } from '@lightspeed/flame/Radio';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { useCapitalTracking } from 'root/apps/CapitalBanner/hooks/';
import { VisibilityTracker } from 'root/apps/tracking/VisibilityTracker';
import { datadogRum } from 'root/datadog/datadogRum';

import { getTrackingEnvironment } from '../tracking/getTrackingEnvironment';
import { TrackingProvider } from '../tracking/TrackingContext';
import TrackingIdentify from '../tracking/TrackingIdentify';
import { CustomModal } from './BusinessSnapshot.style';
import { CapitalOffer, DismissalType } from './BusinessSnapshot.types';
import { CostVsSales } from './CostVsSales';
import { BusinessSnapshotTrackingEvents } from './enums';
import { Stockout } from './Stockout';
import { ChartType, chartVersions, useSnapshotChartData } from './useSnapshotChartData';

const LSPD_CAPITAL_PATH = '/payments/lightspeed-capital';

const LSPD_CAPITAL_DOCS_PARAMS =
  '?utm_medium=cpc&utm_source=google&utm_campaign=NOAM-EN-Search-AW-Performance-Max-ACQ&_bt=&_bk=&_bm=&_bn=x&_bg=&gad_source=1&gclid=CjwKCAjw8rW2BhAgEiwAoRO5rFauEyjZF-3EbTrSuEUCgA3KssiUfUSsTizMWrWf66_sNDFtm3H_aBoCwC4QAvD_BwE';
const LSPD_CAPITAL_DOCS_URL = 'https://www.lightspeedhq.com/pos/retail/retail-capital';
const LSPD_CAPITAL_LOCALE_DOCS: Record<string, string> = {
  'en-us': 'https://www.lightspeedhq.com/pos/retail/retail-capital/',
  'en-ca': 'https://www.lightspeedhq.com/pos/retail/retail-capital/',
  'fr-ca': 'https://fr.lightspeedhq.com/caisse/retail/capital/',
  'en-gb': 'https://www.lightspeedhq.co.uk/pos/retail/capital/',
  'en-au': 'https://www.lightspeedhq.com.au/pos/retail/capital/',
  'nl-nl': 'https://www.lightspeedhq.nl/kassasysteem/retail/capital/',
  'nl-be': 'https://www.lightspeedhq.be/kassasysteem/retail/capital/',
  'fr-be': 'https://fr.lightspeedhq.be/caisse/retail/capital/',
};

type SnapshotAsideProps = {
  isModalOpen: boolean;
  offer: CapitalOffer;
  onRequestClose: (surveyResult: DismissalType) => void;
  chartType: ChartType;
};

export default function SnapshotAside({
  isModalOpen,
  offer,
  onRequestClose,
  chartType,
}: SnapshotAsideProps) {
  const [inSurvey, setInSurvey] = React.useState(false);
  const source = 'reports';
  const { trackCapitalEvent } = useCapitalTracking({ source });

  const lang = window.merchantos.locale.lang.toLowerCase();
  const docPath = LSPD_CAPITAL_LOCALE_DOCS[lang] || LSPD_CAPITAL_DOCS_URL;
  const docUrl = `${docPath}${LSPD_CAPITAL_DOCS_PARAMS}`;
  const { data, isError, isLoading } = useSnapshotChartData(chartType);
  const bannerLocation = 'reports-right-flyout';
  const experimentID = chartVersions[chartType];
  const bannerVersion = data?.data?.chartData?.is_from_vertical
    ? `business-snapshot-v${experimentID}-vertical`
    : `business-snapshot-v${experimentID}-regular`;
  const handleRemindMeLater = (e: React.MouseEvent) => {
    e.preventDefault();
    setInSurvey(true);
  };

  const handleViewOffer = (e: React.MouseEvent) => {
    e.preventDefault();
    trackCapitalEvent(BusinessSnapshotTrackingEvents.BusinessSnapshotClicked, {
      offer_id: offer.UUID,
      banner_location: bannerLocation,
      banner_version: bannerVersion,
      button_type: 'view',
    });
    window.location.href = LSPD_CAPITAL_PATH;
  };

  const handleCapitalLink = (e: React.MouseEvent) => {
    e.preventDefault();
    trackCapitalEvent(BusinessSnapshotTrackingEvents.BusinessSnapshotClicked, {
      offer_id: offer.UUID,
      banner_location: bannerLocation,
      banner_version: bannerVersion,
      button_type: 'info',
    });
    window.open(docUrl, '_blank');
  };

  const handleSurveySubmit: React.FormEventHandler = (e) => {
    e.preventDefault();

    const formData = new FormData(e.target as HTMLFormElement);
    const surveyResult = formData.get('dismissalType') as DismissalType;
    trackCapitalEvent(BusinessSnapshotTrackingEvents.BusinessSnapshotClicked, {
      banner_location: bannerLocation,
      banner_version: bannerVersion,
      button_type: 'dismiss',
      dismissal_type: surveyResult,
    });
    onRequestClose(surveyResult);
  };

  const snapshotAside = () => (
    <CustomModal aside isOpen={isModalOpen} onRequestClose={() => onRequestClose('none')}>
      <ModalHeader showCloseButton />
      <ModalBody style={{ overflowY: 'auto' }}>
        {inSurvey ? (
          <form onSubmit={handleSurveySubmit}>
            <Box mt="16px">
              <Text fontSize="14px" fontWeight={700} lineHeight="18px">
                Let us know the best time to show you your Business Snapshot.{' '}
              </Text>
            </Box>
            <Flex mt="24px" mb="42px" flexDirection="column" style={{ gap: '12px' }}>
              <Radio
                name="dismissalType"
                value="days"
                defaultChecked={true}
                label={
                  <Text fontSize="14px" fontWeight={400}>
                    Show me the Snapshot in a few days.
                  </Text>
                }
              />
              <Radio
                name="dismissalType"
                value="month"
                label={
                  <Text fontSize="14px" fontWeight={400}>
                    Show me the Snapshot next month.
                  </Text>
                }
              />
              <Radio
                name="dismissalType"
                value="offer"
                label={
                  <Text fontSize="14px" fontWeight={400}>
                    Show me the Snapshot once my offer changes.
                  </Text>
                }
              />
            </Flex>
            <Box>
              <Button variant="secondary" fill type="submit">
                Submit
              </Button>
            </Box>
          </form>
        ) : (
          <Flex
            flexDirection="column"
            alignContent="center"
            textAlign="center"
            style={{ gap: '22px' }}
            p="0 14px"
          >
            {chartType === 'costsVsSales' && (
              <CostVsSales
                data={data}
                isError={isError}
                isLoading={isLoading}
                offer={offer}
                onLearnMore={handleCapitalLink}
              />
            )}
            {chartType === 'stockout' && (
              <Stockout
                data={data}
                isError={isError}
                isLoading={isLoading}
                offer={offer}
                onLearnMore={handleCapitalLink}
              />
            )}
            <Box>
              <Button variant="secondary" fill href="#" onClick={handleViewOffer}>
                {chartType === 'costsVsSales' ? 'See my offer' : 'Learn more'}
              </Button>
            </Box>
            <Box>
              <TextLink href="#" onClick={handleRemindMeLater}>
                {chartType === 'costsVsSales' ? 'Remind me later' : 'Not relevant for me'}
              </TextLink>
            </Box>
          </Flex>
        )}
      </ModalBody>
    </CustomModal>
  );

  return (
    <TrackingProvider
      configs={[{ name: 'product', environment: getTrackingEnvironment() }]}
      fallbackRender={snapshotAside}
      onInitError={(err) => datadogRum.addError(err, { context: 'tracking_module_init' })}
    >
      <TrackingIdentify />
      <VisibilityTracker
        id={bannerLocation}
        eventName={BusinessSnapshotTrackingEvents.BusinessSnapshotViewed}
        triggerOnce={true}
        eventData={{
          source,
          banner_location: bannerLocation,
          banner_version: bannerVersion,
        }}
      >
        {snapshotAside()}
      </VisibilityTracker>
    </TrackingProvider>
  );
}
